import React from 'react'
import { FieldArray } from 'react-final-form-arrays'
import { Box, makeStyles, Paper, Typography } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import useTranslate from '../../../../i18n/useTranslate'
import MyButton from '../../../../control/button/button'
import MemberRowInternal from './MemberRowInternal'
import MemberRowExternal from './MemberRowExternal'
import { ParticipantsFormValues, PersonOption, PersonInfo } from '../agendaTypes'
import { useFormState } from 'react-final-form'
import { getIn } from 'final-form'

const SectionArray: React.FC<{
    title: string
    name: keyof ParticipantsFormValues
    prefix?: string
    required?: boolean
    requiredEmail?: boolean
    minItems?: number
    options?: PersonOption[]
    idToEmail?: Record<string, string>
    idToPerson?: Record<string, PersonInfo>
    loading?: boolean
    isExternal?: boolean
    canEdit?: boolean
}> = ({
    title,
    name,
    prefix = 'participants',
    required,
    requiredEmail = false,
    minItems = 0,
    options = [],
    idToEmail = {},
    idToPerson = {},
    loading,
    isExternal = false,
    canEdit = true,
}) => {
    const classes = useStyles()
    const t = useTranslate()
    const base = `${prefix}.${name}`

    const { values } = useFormState({ subscription: { values: true } })
    const sectionValues: any[] = React.useMemo(() => getIn(values, base) || [], [values, base])

    const extractSelectedId = (val: any): string | undefined => {
        if (!val) return
        if (val.userId != null) return String(val.userId)
        const v = Array.isArray(val.user) ? val.user[0] : val.user
        if (v == null) return
        if (typeof v === 'string' || typeof v === 'number') return String(v)
        if (typeof v === 'object') {
            if (v.value != null) return String(v.value)
            if (v.id != null) return String(v.id)
        }
        return
    }

    const selectedIdsSet = React.useMemo(() => {
        const ids = sectionValues
            .map((row) => extractSelectedId(row) ?? extractSelectedId({ user: row?.user, userId: row?.userId }))
            .filter(Boolean)
            .map(String)
        return new Set(ids as string[])
    }, [sectionValues])

    return (
        <Box className={classes.section}>
            <div className={classes.sectionTitleRow}>
                <Typography variant="subtitle2">
                    {title}
                    {required && (
                        <span style={{ color: '#6B7280', marginLeft: 6, fontSize: 12 }}>
                            {t.get('UM.SETTING_AGENDA.PARTICIPANTS.REQUIRED_NOTE')}
                        </span>
                    )}
                </Typography>
                {/* <FieldArray name={base}>
                    {({ fields }) => (
                        <MyButton
                            onClick={() => fields.push({ sendMail: true })}
                            startIcon={<AddIcon />}
                            color="primary"
                            type="button"
                            disabled={!canEdit}
                        >
                            {t.get('UM.SETTING_AGENDA.PARTICIPANTS.ADD_ITEM')}
                        </MyButton>
                    )}
                </FieldArray> */}
            </div>

            <FieldArray name={base}>
                {({ fields }) => {
                    const count =
                        (fields as any)?.length ??
                        (Array.isArray((fields as any)?.value) ? (fields as any).value.length : 0)

                    const disableRemove = minItems > 0 && count <= minItems

                    return (
                        <>
                            {count === 0 && (
                                <Paper
                                    elevation={0}
                                    variant="outlined"
                                    style={{ padding: 16, borderRadius: 8, background: '#F9FAFB' }}
                                >
                                    <Typography variant="body2" color="textSecondary">
                                        {t.get('UM.SETTING_AGENDA.PARTICIPANTS.NO_ITEMS')}
                                    </Typography>
                                </Paper>
                            )}

                            {fields.map((pfx: string, idx: number) => {
                                if (isExternal) {
                                    return (
                                        <MemberRowExternal
                                            key={pfx}
                                            namePrefix={pfx}
                                            requiredEmail={requiredEmail}
                                            onRemove={() => fields.remove(idx)}
                                            disableRemove={disableRemove}
                                            canEdit={canEdit}
                                        />
                                    )
                                }

                                const currentRow = sectionValues?.[idx]
                                const currentId =
                                    extractSelectedId(currentRow) ??
                                    extractSelectedId({ user: currentRow?.user, userId: currentRow?.userId })

                                const filteredOptions = options.filter((opt) => {
                                    const id = String(opt.value)
                                    if (currentId && id === String(currentId)) return true
                                    return !selectedIdsSet.has(id)
                                })

                                return (
                                    <MemberRowInternal
                                        key={pfx}
                                        namePrefix={pfx}
                                        requiredEmail={requiredEmail}
                                        onRemove={() => fields.remove(idx)}
                                        options={filteredOptions}
                                        idToEmail={idToEmail}
                                        idToPerson={idToPerson}
                                        loading={loading}
                                        disableRemove={disableRemove}
                                        defaultPersonValue={currentRow}
                                        canEdit={canEdit}
                                    />
                                )
                            })}
                        </>
                    )
                }}
            </FieldArray>
            
            <FieldArray name={base}>
                {({ fields }) => (
                    <MyButton
                        onClick={() => fields.push({ sendMail: true })}
                        startIcon={<AddIcon />}
                        color="primary"
                        type="button"
                        disabled={!canEdit}
                    >
                        {t.get('UM.SETTING_AGENDA.PARTICIPANTS.ADD_ITEM')}
                    </MyButton>
                )}
            </FieldArray>
        
        </Box>
    )
}

const useStyles = makeStyles((theme) => ({
    section: { padding: theme.spacing(3), borderTop: '1px solid #E5E7EB' },
    sectionTitleRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
}))

export default React.memo(SectionArray)
